import {
    memo,
    useCallback,
    useEffect,
    useState,
} from "react";

import { useNavigate } from "react-router-dom";

import video1 from "../../../assets/video/WhatsApp Video 2025-10-10 at 17.06.49.mp4";
import video2 from "../../../assets/video/WhatsApp Video 2025-10-10 at 17.06.51.mp4";

import poster1 from "../../../assets/images/hero-posters/risky-video-1.webp";
import poster2 from "../../../assets/images/hero-posters/risky-video-2.webp";
import centerHero from "../../../assets/images/hero-posters/risky-center.webp";

import { resolveImageUrl } from "../../../utils/imageUrl";

const API_URL =
    "https://admin.monsinidress.com/api/hero-section/brand";

/* =========================================================
   LOCAL FALLBACK DATA

   These videos are available immediately, so they can
   autoplay as soon as the page opens.
========================================================= */

const FALLBACK_DATA = [
    {
        _id: "static-1",
        brand: "Gowns Collection",

        media: [
            {
                url: video1,
                poster: poster1,
                type: "video",
                filename: "Risky gowns collection",
            },
        ],
    },

    {
        _id: "static-2",
        brand: "Evening Collection",

        media: [
            {
                url: centerHero,
                type: "image",
                filename: "Risky evening gown",
            },
        ],
    },

    {
        _id: "static-3",
        brand: "Gowns Collection",

        media: [
            {
                url: video2,
                poster: poster2,
                type: "video",
                filename: "Risky new arrival gown",
            },
        ],
    },
];

/* =========================================================
   NORMALIZE API DATA
========================================================= */

function normalizeHeroData(raw) {
    if (!Array.isArray(raw) || raw.length === 0) {
        return FALLBACK_DATA;
    }

    return raw.slice(0, 3).map((item, index) => ({
        _id: item?._id || `hero-${index}`,

        brand: item?.brand || "Risky Collection",

        media: Array.isArray(item?.media)
            ? item.media.slice(0, 1).map((mediaItem) => {
                  const rawUrl = mediaItem?.url || "";

                  const absoluteUrl =
                      resolveImageUrl(rawUrl);

                  const type =
                      mediaItem?.type ||
                      (/\.mp4(?:$|\?)/i.test(absoluteUrl)
                          ? "video"
                          : "image");

                  return {
                      ...mediaItem,

                      url: absoluteUrl,

                      type,
                  };
              })
            : [],
    }));
}

/* =========================================================
   HERO SECTION
========================================================= */

function HeroSection({ latestYear = 2026 }) {
    const navigate = useNavigate();

    /*
     * Start with local media immediately.
     *
     * This means the two local videos can begin playing
     * without waiting for API data.
     */
    const [data, setData] =
        useState(FALLBACK_DATA);

    /* =====================================================
       FETCH API DATA

       No click, scroll or touch is required anymore.
    ===================================================== */

    useEffect(() => {
        const controller =
            new AbortController();

        const timer = window.setTimeout(() => {
            controller.abort();
        }, 5000);

        const fetchHeroData = async () => {
            try {
                const response = await fetch(
                    API_URL,
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json",
                        },

                        body: JSON.stringify({
                            brand: "Risky",
                        }),

                        signal: controller.signal,
                    }
                );

                if (!response.ok) {
                    return;
                }

                const result =
                    await response.json();

                const normalizedData =
                    normalizeHeroData(
                        result?.data
                    );

                setData(normalizedData);
            } catch (error) {
                if (
                    error?.name !==
                    "AbortError"
                ) {
                    console.error(
                        "Risky hero request failed:",
                        error
                    );
                }

                /*
                 * If API fails, keep using the
                 * local fallback media.
                 */
            } finally {
                window.clearTimeout(timer);
            }
        };

        fetchHeroData();

        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, []);

    /* =====================================================
       NAVIGATION
    ===================================================== */

    const goToCollection =
        useCallback(() => {
            navigate(
                `/gowns/${latestYear}`
            );
        }, [
            latestYear,
            navigate,
        ]);

    /* =====================================================
       FORCE VIDEO PLAY

       autoPlay + muted normally handles autoplay.

       This additionally asks the browser to start playback
       as soon as enough video data is available.
    ===================================================== */

    const handleVideoReady =
        useCallback((event) => {
            const video =
                event.currentTarget;

            /*
             * Keep it muted because browsers generally
             * require muted video for autoplay.
             */
            video.muted = true;

            const playPromise =
                video.play();

            if (
                playPromise !==
                undefined
            ) {
                playPromise.catch(() => {
                    /*
                     * Browser may temporarily prevent
                     * playback. autoPlay remains enabled.
                     */
                });
            }
        }, []);

    /* =====================================================
       MEDIA RENDERER
    ===================================================== */

    const renderMedia = (
        mediaItem,
        key,
        collectionName,
        collectionIndex
    ) => {
        /* =================================================
           VIDEO
        ================================================= */

        if (
            mediaItem?.type ===
            "video"
        ) {
            /*
             * First video = poster1
             * Third card/video = poster2
             */
            const fallbackPoster =
                collectionIndex === 0
                    ? poster1
                    : poster2;

            return (
                <video
                    key={`${key}-${mediaItem.url}`}
                    src={mediaItem.url}
                    poster={
                        mediaItem.poster ||
                        fallbackPoster
                    }

                    /* =====================================
                       AUTOPLAY SETTINGS
                    ===================================== */
                    autoPlay
                    muted
                    loop
                    playsInline

                    /*
                     * First video starts downloading
                     * immediately.
                     *
                     * Other video also starts automatically,
                     * but metadata helps slightly with
                     * initial page bandwidth.
                     */
                    preload={
                        collectionIndex === 0
                            ? "auto"
                            : "metadata"
                    }

                    onLoadedData={
                        handleVideoReady
                    }

                    onCanPlay={
                        handleVideoReady
                    }

                    disablePictureInPicture

                    aria-hidden="true"

                    className="
                        h-[600px]
                        w-full
                        bg-gray-100
                        object-cover
                        object-top

                        sm:h-[700px]
                        md:h-[800px]
                    "
                />
            );
        }

        /* =================================================
           IMAGE
        ================================================= */

        const isPrimaryImage =
            collectionIndex === 1;

        return (
            <img
                key={key}
                src={mediaItem?.url}
                alt={
                    mediaItem?.filename ||
                    collectionName ||
                    "Risky collection gown"
                }
               

                className="
                    h-[600px]
                    w-full
                    bg-gray-100
                    object-cover
                    object-top

                    sm:h-[700px]
                    md:h-[800px]
                "
            />
        );
    };

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <section
            className="
                flex
                w-full
                flex-col-reverse
                pt-[150px]

                md:h-auto
                md:flex-row
            "
            aria-labelledby="new-arrival-title"
        >
            {/* =================================================
                MEDIA GRID
            ================================================= */}

            <div
                className="
                    grid
                    w-full
                    grid-cols-1
                    gap-2

                    md:w-[85%]
                    md:grid-cols-2

                    lg:grid-cols-3
                "
            >
                {data.map(
                    (
                        collection,
                        collectionIndex
                    ) => (
                        <button
                            type="button"

                            key={
                                collection._id ||
                                collectionIndex
                            }

                            onClick={
                                goToCollection
                            }

                            className="
                                block
                                w-full
                                cursor-pointer
                                text-left

                                focus-visible:outline-none
                                focus-visible:ring-4
                                focus-visible:ring-[#8a459f]
                            "

                            aria-label={`View ${latestYear} ${
                                collection.brand ||
                                "Risky"
                            } collection`}
                        >
                            {collection.media
                                ?.length ? (
                                collection.media.map(
                                    (
                                        mediaItem,
                                        mediaIndex
                                    ) =>
                                        renderMedia(
                                            mediaItem,

                                            `${collectionIndex}-${mediaIndex}`,

                                            collection.brand,

                                            collectionIndex
                                        )
                                )
                            ) : (
                                <div
                                    className="
                                        h-[600px]
                                        w-full
                                        bg-gray-100

                                        sm:h-[700px]
                                        md:h-[800px]
                                    "
                                    aria-hidden="true"
                                />
                            )}
                        </button>
                    )
                )}
            </div>

            {/* =================================================
                NEW ARRIVAL TITLE
            ================================================= */}

            <div
                className="
                    flex
                    h-auto
                    w-full
                    items-center
                    justify-start
                    bg-white
                    py-5
                    text-start

                    md:h-[800px]
                    md:w-[15%]
                    md:justify-center
                    md:py-0
                "
            >
                {/* MOBILE TITLE */}

                <h1
                    id="new-arrival-title"
                    className="
                        block
                        text-[50px]
                        font-lora
                        font-extrabold
                        leading-[45px]
                        tracking-wide
                        text-[#713580]

                        md:hidden
                    "
                >
                    NEW
                    <br />
                    ARRIVAL
                </h1>

                {/* DESKTOP TITLE */}

                <span
                    className="
                        hidden
                        whitespace-nowrap
                        font-lora
                        font-extrabold
                        tracking-widest
                        text-[#713580]

                        md:block
                        md:rotate-[-270deg]
                        md:text-[70px]
                        md:leading-[80px]

                        lg:text-[80px]
                    "
                    aria-hidden="true"
                >
                    NEW ARRIVAL
                </span>
            </div>
        </section>
    );
}

export default memo(HeroSection);